import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { ApiError, api, onSignedOut, session } from '../api/client.ts';

/**
 * Who is signed in, and what they may do.
 *
 * `GET /api/me` is the server's single answer to that, so no screen works it
 * out from the role — the server already refuses what it refuses, and a button
 * that disagrees with the server is a lie either way round.
 */

export interface Me {
  sub: string;
  name: string;
  role: 'viewer' | 'operator' | 'admin' | 'agent';
  can_mark_good: boolean;
  via: 'cognito' | 'dev';
  role_from: 'record' | 'group' | 'default';
  role_disagreement?: string;
}

export interface Can {
  look: boolean;
  launch: boolean;
  mark_good: boolean;
  add_note: boolean;
  assign_vc: boolean;
  import_edit: boolean;
  accept_stale: boolean;
  share: boolean;
  save_filter: boolean;
  admin: boolean;
  new_project: boolean;
}

export interface MenuItem {
  key: string;
  says: string;
  needs: keyof Can;
}

export interface MeAnswer {
  me: Me;
  can: Can;
  labels: Record<string, string>;
  instead_of_action: string | null;
  menu: { tabs: MenuItem[]; more: MenuItem[] };
  nobody_can: string[];
}

type State =
  | { status: 'loading' }
  | { status: 'signed_out' }
  | { status: 'failed'; error: unknown }
  | { status: 'ready'; answer: MeAnswer };

type MeContext = State & {
  reload: () => void;
  signOut: () => void;
};

const Ctx = createContext<MeContext | null>(null);

export function MeProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<State>(
    session.signedIn ? { status: 'loading' } : { status: 'signed_out' }
  );

  const load = useCallback(async () => {
    if (!session.signedIn) {
      setState({ status: 'signed_out' });
      return;
    }
    setState({ status: 'loading' });
    try {
      setState({ status: 'ready', answer: await api.get<MeAnswer>('/api/me') });
    } catch (err) {
      if (err instanceof ApiError && err.signedOut) setState({ status: 'signed_out' });
      else setState({ status: 'failed', error: err });
    }
  }, []);

  useEffect(() => {
    void load();
    // The client tells everybody when a refresh failed, so the whole app shows
    // one "you were signed out" page rather than each screen showing its own.
    return onSignedOut(() => setState({ status: 'signed_out' }));
  }, [load]);

  const signOut = useCallback(() => {
    session.clear();
    setState({ status: 'signed_out' });
  }, []);

  return <Ctx.Provider value={{ ...state, reload: load, signOut }}>{children}</Ctx.Provider>;
}

export function useMe(): MeContext {
  const held = useContext(Ctx);
  if (!held) throw new Error('useMe outside MeProvider');
  return held;
}

/**
 * What this person may do, with everything false until the answer arrives.
 * A screen that showed a launch button while `/api/me` was still loading would
 * flash it at a viewer.
 */
export function useCan(): Can {
  const state = useMe();
  if (state.status === 'ready') return state.answer.can;
  return {
    look: false,
    launch: false,
    mark_good: false,
    add_note: false,
    assign_vc: false,
    import_edit: false,
    accept_stale: false,
    share: false,
    save_filter: false,
    admin: false,
    new_project: false
  };
}
